/**
 * CLOSED-CAGE CLOSING-ENDPOINT — shared trajectory-preparation rule.
 *
 * For a cage/dataset marked CLOSED (`record.cage_closed === true`), all
 * PFD analytical displays MUST stop at the actual closing point:
 *   closing date   = date of the LAST registered HARVEST record
 *   closing weight = harvest average weight recorded on that date
 *
 * Everything after that point is post-harvest noise and must NOT appear
 * on charts, trajectories, comparisons, smoothed curves, or reports.
 *
 * Guardrails
 * ──────────
 *   • Applied purely at the display/data-preparation layer. The
 *     underlying `record.actual_trajectory`, `record.data_points`,
 *     `record.sample_points`, `saved_analysis`, harvest & sample
 *     records, and all biological calculations are NEVER mutated.
 *   • FAIL-SAFE: if a cage is marked CLOSED but has no valid harvest
 *     record, the helpers return the record unchanged — no invented
 *     endpoint. Any downstream consumer keeps its existing behaviour.
 *   • OPEN / ACTIVE cages (`cage_closed !== true`) also return the
 *     record unchanged — zero behavioural drift for open cages.
 *   • The last recorded harvest wins when multiple harvest events
 *     exist — earlier harvests do not determine closure.
 */

/**
 * getClosedCageEndpoint(record)
 *   → { closingDate: ISO string, closingWeight: number, closingDayIndex: int } | null
 */
export function getClosedCageEndpoint(record) {
  if (!record || typeof record !== 'object') return null;
  // Only cages explicitly flagged as CLOSED trigger truncation.
  const isClosed = record.cage_closed === true || record.is_closed === true;
  if (!isClosed) return null;

  const samples = Array.isArray(record.sample_points) ? record.sample_points : [];
  // LAST-BY-DATE harvest entry — do not trust array order.
  let last = null;
  let lastMs = -Infinity;
  for (const sp of samples) {
    if (!sp || sp.type !== 'harvest') continue;
    const w = Number(sp.weight);
    if (!isFinite(w) || w <= 0) continue;
    const t = sp.date ? new Date(sp.date).getTime() : NaN;
    if (!isFinite(t)) continue;
    if (t > lastMs) { lastMs = t; last = sp; }
  }
  if (!last) return null;   // no valid harvest → fail-safe (no endpoint)

  const startIso = record.start_date
    || record.actual_trajectory?.[0]?.date
    || samples[0]?.date;
  const startMs = startIso ? new Date(startIso).getTime() : NaN;
  const closingDayIndex = (isFinite(startMs) && isFinite(lastMs))
    ? Math.round((lastMs - startMs) / 86400000)
    : null;

  return {
    closingDate: last.date,
    closingWeight: Number(last.weight),
    closingDayIndex: (closingDayIndex != null && closingDayIndex >= 0) ? closingDayIndex : null,
  };
}

/**
 * sliceRecordAtClosure(record)
 *   Returns a shallow-cloned record with `actual_trajectory`,
 *   `model_trajectory`, `data_points`, and `sample_points` truncated
 *   at the closing endpoint (inclusive) — but ONLY when the record is
 *   CLOSED and has a valid harvest endpoint. Otherwise returns the
 *   original record reference unchanged.
 *
 *   The original arrays are never mutated (.slice / .filter produce
 *   new arrays); the input record is never modified in place.
 */
export function sliceRecordAtClosure(record) {
  const ep = getClosedCageEndpoint(record);
  if (!ep) return record;                                   // open OR fail-safe
  if (ep.closingDayIndex == null || ep.closingDayIndex < 0) return record;

  const cutIdx = ep.closingDayIndex;                        // inclusive
  const cutMs = new Date(ep.closingDate).getTime();

  const traj = Array.isArray(record.actual_trajectory) ? record.actual_trajectory : null;
  const model = Array.isArray(record.model_trajectory) ? record.model_trajectory : null;
  const dps  = Array.isArray(record.data_points)       ? record.data_points       : null;
  const smp  = Array.isArray(record.sample_points)     ? record.sample_points     : null;

  const clip = (arr) => (arr ? arr.slice(0, cutIdx + 1) : arr);
  const clipSamples = (arr) => (arr
    ? arr.filter((sp) => {
        const t = sp?.date ? new Date(sp.date).getTime() : NaN;
        return isFinite(t) && t <= cutMs;
      })
    : arr);

  return {
    ...record,
    actual_trajectory: traj ? clip(traj) : traj,
    model_trajectory:  model ? clip(model) : model,
    data_points:       dps ? clip(dps) : dps,
    sample_points:     smp ? clipSamples(smp) : smp,
    // For downstream inspection only — never persisted.
    _closureApplied: true,
    _closureEndpoint: ep,
  };
}
